import React, { useEffect, useState } from 'react';
import {
  Activity,
  Bell,
  ChevronDown,
  CircleHelp,
  Flag,
  FlaskConical,
  HeartPulse,
  LayoutDashboard,
  LogOut,
  Menu,
  MessageSquare,
  Network,
  Receipt,
  RefreshCw,
  Repeat2,
  SearchCheck,
  ShieldCheck,
  Users,
  X,
} from 'lucide-react';
import api, { getAdminSession, saveAdminSession, clearAdminSession } from './lib/adminApi';
import config from './config';
import { Button } from './components/ui/button';
import AdminLegacyPanels, { LegacySection } from './AdminLegacyPanels';
import Overview from './admin/Overview';
import LatestActivity from './admin/LatestActivity';
import Bills from './admin/Bills';
import ActivityPanel from './admin/Activity';
import UsersPanel, { UserInspector } from './admin/Users';
import Receipts, { BillInspector } from './admin/Receipts';
import ProductionState from './admin/ProductionState';
import FlowHealth from './admin/FlowHealth';
import { dateKey, message, Period, recentPeriod } from './admin/api';
import { Notice } from './admin/shared';
import mark from './assets/brand/whole-table.png';
import './design/tokens.css';
import './admin/admin.css';
type Section = 'overview' | 'receipts' | 'users' | 'bills' | 'activity' | 'production' | LegacySection;
const groups = [
  {
    label: 'Workspace',
    items: [
      {
        id: 'overview',
        title: 'Overview',
        icon: LayoutDashboard,
      },
      {
        id: 'receipts',
        title: 'Receipts',
        icon: SearchCheck,
      },
      {
        id: 'users',
        title: 'Users',
        icon: Users,
      },
      {
        id: 'bills',
        title: 'Bills',
        icon: Receipt,
      },
    ],
  },
  {
    label: 'Operations',
    items: [
      {
        id: 'production',
        title: 'Production state',
        icon: HeartPulse,
      },
      {
        id: 'recurring',
        title: 'Recurring',
        icon: Repeat2,
      },
      {
        id: 'notifications',
        title: 'Notifications',
        icon: Bell,
      },
      {
        id: 'feedback',
        title: 'Feedback',
        icon: MessageSquare,
      },
      {
        id: 'activity',
        title: 'Activity',
        icon: Activity,
      },
    ],
  },
  {
    label: 'Configuration',
    items: [
      {
        id: 'features',
        title: 'Feature flags',
        icon: Flag,
      },
      {
        id: 'sandbox',
        title: 'Receipt playground',
        icon: FlaskConical,
      },
    ],
  },
];
const labels: Record<Section, [string, string]> = {
  overview: ['Overview', 'How people split, settle, and use VibeSplit.'],
  receipts: ['Receipts', 'Catch processing problems. Understand extraction quality.'],
  users: ['Users', 'Understand a person’s account and activity.'],
  bills: ['Bills', 'Find bills and investigate their state.'],
  production: ['Production state', 'Deployed version, golden-flow canaries, and bill completion health.'],
  recurring: ['Recurring', 'Monitor schedules and the bills they create.'],
  notifications: ['Notifications', 'Trace messages from dispatch to delivery.'],
  feedback: ['Feedback', 'Read what people are telling us.'],
  activity: ['Activity', 'Explore recorded app events.'],
  features: ['Feature flags', 'Manage access to app features.'],
  sandbox: ['Receipt playground', 'Try an extraction without creating a real bill.'],
  trips: ['Trips', 'Inspect legacy trip records.'],
  social: ['Relationships', 'Explore the existing bill relationship graph.'],
};
function readLocation() {
  const [raw, query] = window.location.hash.slice(1).split('?');
  const section = raw === 'stats' || !raw || !(raw in labels) ? 'overview' : (raw as Section);
  return {
    section,
    params: new URLSearchParams(query),
  };
}
export default function AdminDashboard() {
  const [authenticated, setAuthenticated] = useState(!!getAdminSession()),
    [password, setPassword] = useState(''),
    [loginError, setLoginError] = useState(''),
    [signingIn, setSigningIn] = useState(false);
  const [location, setLocation] = useState(readLocation),
    [refresh, setRefresh] = useState(0),
    [menu, setMenu] = useState(false),
    [period, setPeriod] = useState<Period>(() => recentPeriod(7)),
    [preset, setPreset] = useState('7'),
    [draft, setDraft] = useState(period),
    [rangeError, setRangeError] = useState('');
  const section = location.section,
    userId = location.params.get('user'),
    billId = location.params.get('bill');
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [section]);
  const receiptFilter = location.params.get('filter') || 'attention';
  const navigate = (next: Section, params: Record<string, string> = {}) => {
    window.location.hash =
      next + (Object.keys(params).length ? `?${new URLSearchParams(params)}` : '');
    setMenu(false);
  };
  const inspect = (key: 'user' | 'bill', id: string) => {
    const params = Object.fromEntries(location.params.entries());
    delete params.user;
    delete params.bill;
    navigate(section, {
      ...params,
      [key]: id,
    });
  };
  const closeInspector = () => {
    const params = Object.fromEntries(location.params.entries());
    delete params.user;
    delete params.bill;
    navigate(section, params);
  };
  useEffect(() => {
    const update = () => setLocation(readLocation());
    window.addEventListener('hashchange', update);
    const expired = () => {
      setAuthenticated(false);
      setPassword('');
      setLoginError('Your session expired. Sign in again.');
    };
    window.addEventListener('vibesplit-admin-expired', expired);
    const syncSession = () => setAuthenticated(!!getAdminSession());
    window.addEventListener('storage', syncSession);
    window.addEventListener('focus', syncSession);
    return () => {
      window.removeEventListener('storage', syncSession);
      window.removeEventListener('focus', syncSession);
      window.removeEventListener('hashchange', update);
      window.removeEventListener('vibesplit-admin-expired', expired);
    };
  }, []);
  const retry = () => setRefresh((n) => n + 1);
  const login = async (event: React.FormEvent) => {
    event.preventDefault();
    if (signingIn) return;
    setSigningIn(true);
    setLoginError('');
    try {
      const { data } = await api.post(`${config.apiBaseUrl}/admin/verify-password`, {
        password,
      });
      if (!data?.token) throw new Error('Sign-in failed');
      saveAdminSession(data.token);
      setPassword('');
      setAuthenticated(true);
    } catch (e: any) {
      setLoginError(
        e?.response?.status === 401
          ? 'That password didn’t match. Try again.'
          : e?.response?.status === 429
            ? 'Too many sign-in attempts. Try again later.'
            : message(e)
      );
    } finally {
      setSigningIn(false);
    }
  };
  const env =
    config.apiBaseUrl.includes('localhost') || config.apiBaseUrl.includes('127.0.0.1')
      ? 'Local'
      : config.apiBaseUrl.includes('dev.')
        ? 'Development'
        : 'Production';
  const brand = (
    <div className="admin-brand">
      <img src={mark} alt="" />
      <span>
        VibeSplit<small>Admin workspace</small>
      </span>
    </div>
  );
  if (!authenticated)
    return (
      <main className="vs-design admin-ui admin-login">
        <section className="admin-login-card">
          {brand}
          <span className="admin-login-icon">
            <ShieldCheck size={25} />
          </span>
          <h1>
            A clearer view of
            <br />
            every split.
          </h1>
          <p>Sign in to your admin workspace. Stay signed in for 30 days on this browser.</p>
          <form onSubmit={login}>
            <label>
              Admin password
              <input
                autoFocus
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </label>
            {loginError && <Notice danger>{loginError}</Notice>}
            <Button type="submit" disabled={signingIn}>
              {signingIn ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>
          <small>{env} environment · Access is restricted to admins</small>
        </section>
      </main>
    );
  const dated = section === 'overview' || section === 'receipts';
  return (
    <div className="vs-design admin-ui admin-shell">
      <a
        className="admin-skip"
        href="#admin-content"
        onClick={(event) => {
          event.preventDefault();
          document.getElementById('admin-content')?.focus();
        }}
      >
        Skip to content
      </a>
      <aside
        onKeyDown={(e) => {
          if (e.key === 'Escape') setMenu(false);
        }}
        className={`admin-sidebar ${menu ? 'is-open' : ''}`}
      >
        {brand}
        <button
          className="admin-menu-close admin-icon-button"
          onClick={() => setMenu(false)}
          aria-label="Close navigation"
        >
          <X size={20} />
        </button>
        <nav aria-label="Admin navigation">
          {groups.map((group) => (
            <div className="admin-nav-group" key={group.label}>
              <p>{group.label}</p>
              {group.items.map(({ id, title, icon: Icon }) => (
                <a
                  key={id}
                  href={`#${id}`}
                  onClick={() => setMenu(false)}
                  aria-current={section === id ? 'page' : undefined}
                >
                  <Icon size={18} />
                  <span>{title}</span>
                  {section === id && <span className="admin-nav-dot" />}
                </a>
              ))}
            </div>
          ))}
          <details className="admin-archive">
            <summary>
              <CircleHelp size={16} />
              Legacy records
              <ChevronDown size={14} />
            </summary>
            <a href="#trips" aria-current={section === 'trips' ? 'page' : undefined}>
              Trips
            </a>
            <a href="#social" aria-current={section === 'social' ? 'page' : undefined}>
              <Network size={16} />
              Relationships
            </a>
          </details>
        </nav>
        <div className="admin-sidebar-bottom">
          <span className="admin-environment">
            <i />
            {env}
          </span>
          <button
            onClick={() => {
              clearAdminSession();
              setAuthenticated(false);
            }}
          >
            <LogOut size={16} />
            Sign out
          </button>
        </div>
      </aside>
      {menu && (
        <button
          className="admin-menu-backdrop"
          aria-label="Close navigation"
          onClick={() => setMenu(false)}
        />
      )}
      <main id="admin-content" className="admin-main" tabIndex={-1}>
        <header className="admin-page-header">
          <div className="admin-heading-group">
            <button
              className="admin-menu-open admin-icon-button"
              aria-label="Open navigation"
              aria-expanded={menu}
              onClick={() => setMenu(true)}
            >
              <Menu size={21} />
            </button>
            <div>
              <span className="admin-eyebrow">ADMIN WORKSPACE</span>
              <h1>{labels[section][0]}</h1>
              <p>{labels[section][1]}</p>
            </div>
          </div>
          <Button variant="outline" onClick={retry}>
            <RefreshCw size={15} />
            Refresh
          </Button>
        </header>
        {dated && (
          <div className="admin-period-bar">
            <div className="admin-actions">
              <div className="admin-segments" aria-label="Reporting period">
                {['7', '30', '90'].map((days) => (
                  <button
                    key={days}
                    aria-pressed={preset === days}
                    onClick={() => {
                      setPreset(days);
                      setPeriod({
                        ...recentPeriod(Number(days)),
                        includeTestData: period.includeTestData,
                      });
                      setRangeError('');
                    }}
                  >
                    Last {days} days
                  </button>
                ))}
                <button
                  aria-pressed={preset === 'custom'}
                  onClick={() => {
                    setPreset('custom');
                    setDraft(period);
                  }}
                >
                  Custom
                </button>
              </div>
              <span className="admin-muted">
                {period.start} – {period.end} · UTC
              </span>
            </div>
            <label className="admin-check-label">
              <input
                type="checkbox"
                checked={period.includeTestData}
                onChange={(e) =>
                  setPeriod({
                    ...period,
                    includeTestData: e.target.checked,
                  })
                }
              />
              Include test bills
            </label>
            {preset === 'custom' && (
              <form
                className="admin-custom-range"
                onSubmit={(e) => {
                  e.preventDefault();
                  const span = Date.parse(draft.end) - Date.parse(draft.start);
                  if (
                    !Number.isFinite(span) ||
                    span < 0 ||
                    span > 89 * 86400000 ||
                    draft.end > dateKey(new Date())
                  ) {
                    setRangeError('Choose up to 90 days, ending today or earlier.');
                    return;
                  }
                  setRangeError('');
                  setPeriod({
                    ...draft,
                    includeTestData: period.includeTestData,
                  });
                }}
              >
                <label>
                  From
                  <input
                    type="date"
                    value={draft.start}
                    max={draft.end}
                    required
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        start: e.target.value,
                      })
                    }
                  />
                </label>
                <label>
                  To
                  <input
                    type="date"
                    value={draft.end}
                    min={draft.start}
                    max={dateKey(new Date())}
                    required
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        end: e.target.value,
                      })
                    }
                  />
                </label>
                <Button variant="outline" type="submit">
                  Apply dates
                </Button>
                {rangeError && <p role="alert">{rangeError}</p>}
              </form>
            )}
          </div>
        )}
        {section === 'overview' ? (
          <Overview
            period={period}
            refresh={refresh}
            retry={retry}
            onBill={(id) => inspect('bill', id)}
            onUser={(id) => inspect('user', id)}
            onReceipts={(filter) =>
              navigate('receipts', {
                filter,
              })
            }
          >
            <LatestActivity refresh={refresh} onBill={(id) => inspect('bill', id)} />
          </Overview>
        ) : section === 'users' ? (
          <UsersPanel refresh={refresh} retry={retry} onUser={(id) => inspect('user', id)} />
        ) : section === 'receipts' ? (
          <Receipts
            period={period}
            filter={receiptFilter}
            setFilter={(filter) =>
              navigate('receipts', {
                filter,
              })
            }
            refresh={refresh}
            retry={retry}
            onBill={(id) => inspect('bill', id)}
            onUser={(id) => inspect('user', id)}
          />
        ) : section === 'bills' ? (
          <Bills
            refresh={refresh}
            retry={retry}
            onBill={(id) => inspect('bill', id)}
            onUser={(id) => inspect('user', id)}
          />
        ) : section === 'production' ? (
          <div className="admin-stack">
            <ProductionState refresh={refresh} retry={retry} />
            <FlowHealth refresh={refresh} />
          </div>
        ) : section === 'activity' ? (
          <ActivityPanel
            refresh={refresh}
            retry={retry}
            onBill={(id) => inspect('bill', id)}
            onUser={(id) => inspect('user', id)}
          />
        ) : (
          <AdminLegacyPanels
            key={section}
            section={section}
            refreshKey={refresh}
            notificationUser={
              location.params.has('recipient')
                ? {
                    id: location.params.get('recipient')!,
                    name: location.params.get('name') || 'Selected user',
                  }
                : undefined
            }
            onUser={(id) => inspect('user', id)}
            onBill={(id) => inspect('bill', id)}
          />
        )}
        <footer className="admin-footer">
          VibeSplit · Admin workspace<span>Amounts stay in their original currency.</span>
        </footer>
      </main>
      {userId && (
        <UserInspector
          key={userId}
          id={userId}
          onClose={closeInspector}
          onBill={(id) => inspect('bill', id)}
          onNotifications={(id, name) =>
            navigate('notifications', {
              recipient: id,
              name,
            })
          }
          onChanged={retry}
        />
      )}
      {billId && (
        <BillInspector
          key={billId}
          id={billId}
          onClose={closeInspector}
          onUser={(id) => inspect('user', id)}
          onChanged={retry}
        />
      )}
    </div>
  );
}
